import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { usePermissionStore } from './usePermissionStore';
import { setSinkCorrelationId } from '../lib/observability/sink';
import { API_BASE } from '../config/runtimeConfig';
import { identifyClarityUser, trackClarityEvent } from '../lib/observability/clarity';
import { getAnonCid } from '../lib/observability/anon';

// Auto-clear corrupted state on load.
try {
  const stored = localStorage.getItem('bizcore-auth');
  if (stored) {
    const parsed = JSON.parse(stored);
    const state = parsed?.state;
    if (state?.isAuthenticated && !state?.user) {
      localStorage.removeItem('bizcore-auth');
    }
  }
} catch {
  localStorage.removeItem('bizcore-auth');
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: number;
  currentTenantId: string | null;
  isFirstLogin: boolean;
}

interface Business {
  id: string;
  tenantId: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  district?: string | null;
  businessType?: string;
  panNumber?: string | null;
  isVATRegistered?: boolean;
  vatNumber?: string | null;
  currency?: string;
  logoUrl?: string | null;
  website?: string | null;
  description?: string | null;
  onboardingStatus?: string | null;
}

interface ModuleInfo {
  id: string;
  name: string;
  code: string;
  description: string;
  icon: string;
  sortOrder: number;
  isActive: boolean;
  isEnabled: boolean;
}

interface AuthState {
  user: User | null;
  business: Business | null;
  businessName: string;
  businessId: string | null;
  modules: ModuleInfo[];
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  hasHydrated: boolean;
  isBootstrapped: boolean;
  bootstrapError: string | null;
  initAuth: () => Promise<void>;
  bootstrap: () => Promise<void>;
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  impersonate: (accessToken: string) => boolean;
  setTokens: (accessToken: string, refreshToken: string | null) => void;
  setHydrated: (hydrated: boolean) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  isSuperAdmin: () => boolean;
  isOwner: () => boolean;
  isAdmin: () => boolean;
  isAccountant: () => boolean;
  isSales: () => boolean;
  isPOSOperator: () => boolean;
  isHR: () => boolean;
  isStaff: () => boolean;
  isManagement: () => boolean;
}

const ROLE_MAP: Record<string, number> = {
  'SuperAdmin': 0,
  'Admin': 1,
  'Accountant': 2,
  'Sales': 3,
  'POSOperator': 4,
  'HRManager': 6,
  'Owner': 5,
};

const NORMALIZED_ROLE_MAP: Record<string, number> = {
  superadmin: 0,
  super_admin: 0,
  'super admin': 0,
  admin: 1,
  accountant: 2,
  sales: 3,
  posoperator: 4,
  'pos operator': 4,
  hrmanager: 6,
  'hr manager': 6,
  owner: 5,
};

const isValidRole = (role: number): role is 0 | 1 | 2 | 3 | 4 | 5 | 6 => [0, 1, 2, 3, 4, 5, 6].includes(role);

const parseRole = (raw: unknown): number => {
  if (raw === null || raw === undefined) return -1;
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const parsed = parseRole(item);
      if (isValidRole(parsed)) return parsed;
    }
    return -1;
  }
  const num = Number(raw);
  if (!isNaN(num) && isValidRole(num)) return num;
  if (typeof raw === 'string') {
    const normalized = raw.trim();
    const mapped = ROLE_MAP[normalized] ?? NORMALIZED_ROLE_MAP[normalized.toLowerCase()];
    return mapped ?? -1;
  }
  return -1;
};

const base64UrlDecode = (str: string) => {
  try {
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    return JSON.parse(atob(padded));
  } catch (e) {
    console.error('[Auth] Decoding failed:', e);
    return null;
  }
};

const decodeToken = (token: string) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const decoded = base64UrlDecode(parts[1]);
    if (!decoded) return null;
    const roleClaim = decoded.role ?? decoded.Role ?? decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    return {
      id: decoded.uid,
      email: decoded.email,
      role: parseRole(roleClaim),
      tenantId: decoded.tid,
    };
  } catch {
    return null;
  }
};

// Always-available core modules. Used only as a fail-safe when /bootstrap is unreachable —
// we never fall OPEN to the full module set; real entitlements come from the backend.
const CORE_MODULES = ['dashboard', 'settings'];

const authHttp = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

let bootstrapInFlight: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      business: null,
      businessName: '',
      businessId: null,
      modules: [],
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isImpersonating: false,
      hasHydrated: false,
      isBootstrapped: false,
      bootstrapError: null,
      initAuth: async () => {
        const state = get();
        if (state.isBootstrapped) return;
        // No in-memory access token check here: a normal reloaded session has no token
        // in memory (it lives in the HttpOnly cookie). Bootstrap revalidates via the cookie.
        if (!state.isAuthenticated) {
          set({ isBootstrapped: true });
          return;
        }
        await get().bootstrap();
      },
      bootstrap: async () => {
        const state = get();
        if (state.isBootstrapped) return;
        if (bootstrapInFlight) return bootstrapInFlight;

        bootstrapInFlight = (async () => {
          const timeoutId = setTimeout(() => {
            set({ isBootstrapped: true, bootstrapError: null });
          }, 8000);

          try {
            // The access token may be absent in memory (normal reloaded session: it lives
            // only in the HttpOnly cookie). When present (impersonation) we can derive the
            // role from it; otherwise fall back to the persisted user role. The session is
            // ultimately validated by the /bootstrap call below (authenticated via cookie).
            const decoded = state.accessToken ? decodeToken(state.accessToken) : null;
            if (state.accessToken && !decoded) {
              // We had a token but it is unparseable -> it cannot authenticate anything.
              get().logout();
              return;
            }
            const roleLabel = String(decoded?.role ?? state.user?.role ?? '');

            try {
              // Single source of truth: real role, permissions, enabled modules + business.
              const response = await authHttp.get('/bootstrap', {
                timeout: 5000,
                // Cross-site cookie isn't sent (SameSite=Lax), so authenticate bootstrap
                // via the Authorization header like every other request.
                headers: state.accessToken ? { Authorization: `Bearer ${state.accessToken}` } : undefined,
              });
              const data = response.data?.data ?? response.data ?? {};
              const enabled: string[] = Array.isArray(data.enabledModules) ? data.enabledModules : [];
              usePermissionStore.getState().setBootstrapData({
                role: String(data.role ?? roleLabel),
                permissions: Array.isArray(data.permissions) ? data.permissions : [],
                // core modules stay available even if the backend omits them
                enabledModules: Array.from(new Set([...CORE_MODULES, ...enabled])),
                modules: Array.isArray(data.modules) ? data.modules : [],
              });
              // Opaque, backend-provided correlation id: key first-party telemetry by it AND
              // (opt-in) link the Clarity session to the same id. Absent → events send cid-less.
              const correlationId = typeof data.correlationId === 'string' ? data.correlationId : undefined;
              if (correlationId) {
                setSinkCorrelationId(correlationId);
                identifyClarityUser({ customId: correlationId });
                // Stitch anon → user: now that the user cid is set, emit an authed event carrying
                // the anon id so the backend can union pre-auth + post-auth behavior.
                trackClarityEvent('identity-stitched', { anon_cid: getAnonCid() });
              }
              const business = data.business;
              if (business) {
                set({ business, businessName: business.name ?? '', businessId: business.id ?? null });
              }
            } catch (err) {
              // A real 401 means the cookie session is invalid/expired -> log out (never
              // fail-open into the app). Any other error (network, 5xx, timeout) is treated
              // as "/bootstrap unreachable": fail-SAFE to token role + core modules only.
              const status = (err as { response?: { status?: number } })?.response?.status;
              if (status === 401) {
                clearTimeout(timeoutId);
                get().logout();
                return;
              }
              // Degraded, NOT a plan restriction: leave entitlements UNKNOWN (isLoaded=false) so
              // guarded modules show a retryable "unavailable" state rather than a paywall.
              usePermissionStore.getState().markDegraded(roleLabel);
              set({ bootstrapError: 'Unable to load permissions. Some modules may be unavailable.' });
            }

            set({ isBootstrapped: true, bootstrapError: null });
          } finally {
            clearTimeout(timeoutId);
            set({ isBootstrapped: true });
          }
        })().finally(() => {
          bootstrapInFlight = null;
        }) as Promise<void>;

        return bootstrapInFlight;
      },
      setAuth: (user, accessToken, refreshToken) => {
        const decoded = decodeToken(accessToken);
        const role = parseRole(decoded?.role ?? user.role);
        if (!isValidRole(role)) {
          set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
          return;
        }
        set({
          user: { ...user, role },
          business: null,
          modules: [],
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isBootstrapped: false,
          bootstrapError: null,
        });
      },
      impersonate: (accessToken) => {
        // SuperAdmin handoff: only an access token is provided (no refresh
        // token, no user object). Build a minimal user from the JWT claims and
        // populate the store the same way a normal login does, then mark the
        // session as impersonated so the banner shows. The real role,
        // permissions and business are filled in by bootstrap() afterwards.
        const decoded = decodeToken(accessToken);
        const role = parseRole(decoded?.role);
        if (!decoded || !isValidRole(role)) {
          return false;
        }
        const user: User = {
          id: decoded.id ?? '',
          firstName: '',
          lastName: '',
          email: decoded.email ?? '',
          role,
          currentTenantId: decoded.tenantId ?? null,
          isFirstLogin: false,
        };
        get().setAuth(user, accessToken, '');
        if (!get().isAuthenticated) {
          return false;
        }
        set({ isImpersonating: true });
        return true;
      },
      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken: refreshToken || get().refreshToken });
      },
      setHydrated: (hydrated) => set({ hasHydrated: hydrated }),
      logout: () => {
        // Clean up persisted auth state (tokens live in HttpOnly cookies, not localStorage)
        localStorage.removeItem('bizcore-auth');
        localStorage.removeItem('loginRedirect');
        sessionStorage.clear();
        usePermissionStore.getState().reset();
        set({
          user: null,
          business: null,
          businessName: '',
          businessId: null,
          modules: [],
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isImpersonating: false,
          isBootstrapped: false,
          bootstrapError: null,
        });
      },
      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),
      isSuperAdmin: () => get().user?.role === 0,
      isOwner: () => get().user?.role === 5,
      isAdmin: () => {
        const role = get().user?.role;
        return role === 1 || role === 5;
      },
      isAccountant: () => get().user?.role === 2,
      isSales: () => get().user?.role === 3,
      isPOSOperator: () => get().user?.role === 4,
      isHR: () => {
        const role = get().user?.role;
        return role === 6 || role === 5;
      },
      isStaff: () => {
        const role = get().user?.role;
        return role !== undefined && role !== null && isValidRole(role) && role !== 0;
      },
      isManagement: () => {
        const role = get().user?.role;
        return [0, 1, 5, 6].includes(role ?? -1);
      },
    }),
    {
      name: 'bizcore-auth',
      partialize: (state) => ({
        user: state.user,
        business: state.business,
        businessName: state.businessName,
        businessId: state.businessId,
        modules: state.modules,
        isAuthenticated: state.isAuthenticated,
        isImpersonating: state.isImpersonating,
      }),
    }
  )
);

const handlePostHydration = () => {
  const state = useAuthStore.getState();
  // A persisted authed session without a user object is unusable -> wipe it.
  // We do NOT wipe for a missing accessToken: the access token is never persisted
  // (it lives only in the HttpOnly cookie), so on reload it is always absent in memory.
  // Bootstrap will revalidate the session against the cookie (200 -> stay, 401 -> logout).
  if (state.isAuthenticated && !state.user) {
    useAuthStore.setState({
      user: null, business: null, businessName: '', businessId: null, modules: [],
      accessToken: null, refreshToken: null, isAuthenticated: false, isImpersonating: false,
      isBootstrapped: false, bootstrapError: null, hasHydrated: true,
    });
    return;
  }

  if (state.isAuthenticated) {
    // Authed (cookie session or impersonation) -> run bootstrap to confirm/enrich.
    useAuthStore.setState({ isBootstrapped: false, hasHydrated: true });
  } else {
    useAuthStore.setState({ hasHydrated: true, isBootstrapped: true });
  }
};

if (useAuthStore.persist?.hasHydrated?.()) {
  // Synchronous storage (localStorage) finishes rehydrating during store creation, so an
  // onFinishHydration listener registered now would never fire — leaving hasHydrated false
  // until the 3s failsafe, which also sets isBootstrapped=true and thus SKIPS the network
  // bootstrap (permissions/modules never load on reload). Run post-hydration directly.
  handlePostHydration();
} else if (useAuthStore.persist?.onFinishHydration) {
  useAuthStore.persist.onFinishHydration(handlePostHydration);
} else {
  handlePostHydration();
}

// Failsafe: if hydration never completes, force it after 3 seconds
setTimeout(() => {
  const state = useAuthStore.getState();
  if (!state.hasHydrated) {
    useAuthStore.setState({ hasHydrated: true, isBootstrapped: true });
  }
}, 3000);
